import type React from 'react';
import type { ChatSession } from '../../types/chat-storage';
import SessionHeader from '../molecules/SessionHeader';
import SessionContent from '../molecules/SessionContent';
import SessionFooter from '../molecules/SessionFooter';
import SessionEditForm from '../molecules/SessionEditForm';
import IconButton from '../atoms/IconButton';

export interface SessionWidgetProps extends React.HTMLAttributes<HTMLDivElement> {
  session: ChatSession;
  isActive?: boolean;
  isEditing?: boolean;
  editValue?: string;
  onSelect?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  onEditValueChange?: (value: string) => void;
  getMessagePreview?: (session: ChatSession) => string;
  formatDate?: (date: Date) => string;
}

const defaultGetMessagePreview = (session: ChatSession): string => {
  const lastMessage = session.messages[session.messages.length - 1];
  if (!lastMessage) {return 'No messages yet';}
  
  const textWidget = lastMessage.widgets?.find(w => w.type === 'text');
  if (textWidget) {
    return textWidget.text.slice(0, 60) + (textWidget.text.length > 60 ? '...' : '');
  }
  
  return 'Sent a message';
};

const defaultFormatDate = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  if (days === 0) {return 'Today';}
  if (days === 1) {return 'Yesterday';}
  if (days < 7) {return `${days} days ago`;}
  return date.toLocaleDateString();
};

export default function SessionWidget({
  session,
  isActive = false,
  isEditing = false,
  editValue = '',
  onSelect,
  onEdit,
  onDelete,
  onSaveEdit,
  onCancelEdit,
  onEditValueChange,
  getMessagePreview = defaultGetMessagePreview,
  formatDate = defaultFormatDate,
  className = '',
  ...props
}: SessionWidgetProps) {
  const handleClick = () => {
    if (!isEditing && onSelect) {
      onSelect();
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  const handleEditFormClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const sessionActions = (
    <>
      <IconButton
        onClick={handleEditClick}
        variant="ghost"
        size="small"
        title="Rename chat"
        aria-label="Rename chat"
        icon={
          <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        }
      />
      <IconButton
        onClick={handleDeleteClick}
        variant="ghost"
        size="small"
        title="Delete chat"
        aria-label="Delete chat"
        className="hover:bg-danger-hover text-danger hover:text-danger-border"
        icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        }
      />
    </>
  );

  return (
    <div
      className={`sidebar-item ${
        isActive ? 'sidebar-item-active' : ''
      } ${className}`}
      onClick={handleClick}
      {...props}
    >
      {isEditing ? (
        <SessionEditForm
          value={editValue}
          onChange={onEditValueChange ?? (() => {})}
          onSave={onSaveEdit ?? (() => {})}
          onCancel={onCancelEdit ?? (() => {})}
          placeholder="Enter session title..."
          onClick={handleEditFormClick}
        />
      ) : (
        <div className="group w-full">
          <SessionHeader
            title={session.title}
            status="active"
            actions={sessionActions}
          />
          <SessionContent preview={getMessagePreview(session)} />
          <SessionFooter
            date={formatDate(session.updatedAt)}
            messageCount={session.messages.length}
          />
        </div>
      )}
    </div>
  );
}